import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useSyncPreviousValue } from 'components/hooks';
import { hasPiiQueryParam, omitPiiFromQueryString, omitPiiQueryParams } from 'modules/shared/utils/piiQueryParams';

const buildUrl = (query: string): string =>
  window.location.pathname + (query ? `?${query}` : '') + window.location.hash;

/**
 * useUrlQueryParams takes in an object and watches for changes. When there is change in the
 * state it handles updating the URL with those changes.
 *
 * Keys in PII_QUERY_PARAM_DENYLIST are omitted. Navigation is shallow so they are not
 * replayed in a `/_next/data/<buildId>/<route>.json?<query>` request either.
 * @param state
 */
export const useUrlQueryParams = (state, withQueryParams) => {
  const router = useRouter();

  const currentState = JSON.stringify(state);
  const prevState = useSyncPreviousValue(currentState);

  // Cleans a URL handed to us. Deliberately not gated on `withQueryParams`, which only means
  // "do not write filters to the URL".
  useEffect(() => {
    if (hasPiiQueryParam(window.location.search)) {
      router.replace(buildUrl(omitPiiFromQueryString(window.location.search)), undefined, { shallow: true });
    }
    // Mount only: cleans the URL we arrived with, not later filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (withQueryParams && prevState !== currentState) {
      router.replace(buildUrl(new URLSearchParams(omitPiiQueryParams(state)).toString()), undefined, {
        shallow: true,
      });
    }
  }, [currentState]);
};
